import { ChartComponentParams } from "charts/src/types/chart";
import { useCallback } from "react";

import {
  addMessage,
  resetIsStreaming,
  updateMessage,
} from "@/context/redux/reducers/chat-slice";
import { Oriented } from "@/data/constants/oriented";
import { Translation } from "@/data/lang";
import { useAppDispatch } from "@/hooks/redux-hooks";

import ReplyChartView from "../components/chart/ReplyChartView";
import { ProcessingStatusView } from "../components/message-widgets/ProcessingStatusView";
import { RetryErrorView } from "../components/session-widgets/RetryErrorView";
import FeedbackButtons from "../components/session-widgets/feedbackButtons";
import { ProcessingStatusData } from "../services/chat-stream";

export const useStreamActions = (t: Translation) => {
  const dispatch = useAppDispatch();

  const initMessage = useCallback(
    ({ messageId }: { messageId: string }) => {
      dispatch(addMessage({ messageId, type: "gpt", content: "" }));
    },
    [dispatch]
  );

  const handleStreamChunk = useCallback(
    ({ chunk, messageId }: { chunk: string; messageId: string }) => {
      dispatch(updateMessage({ messageId, content: chunk }));
    },
    [dispatch]
  );

  const handleProcessingStatus = useCallback(
    ({
      status,
      messageId,
    }: {
      status: ProcessingStatusData[];
      messageId: string;
    }) => {
      if (status.length === 0) return;
      dispatch(
        updateMessage({
          messageId,
          content: <ProcessingStatusView status={status} />,
        })
      );
    },
    [dispatch]
  );

  const handleCompleteStream = useCallback(
    ({ messageId }: { messageId: string }) => {
      dispatch(
        addMessage({
          type: "element",
          content: <FeedbackButtons messageId={messageId} value={null} />,
        })
      );
      dispatch(resetIsStreaming());
    },
    [dispatch]
  );

  const handleStreamError = useCallback(
    ({
      query,
      investStyle,
    }: {
      query: string;
      investStyle: Oriented | null;
    }) => {
      dispatch(
        addMessage({
          type: "element",
          content: <RetryErrorView {...{ query, investStyle }} />,
        })
      );
    },
    [dispatch]
  );

  const handleOutputError = useCallback(
    ({
      messageId,
      query,
      investStyle,
    }: {
      messageId: string;
      query: string;
      investStyle: Oriented | null;
    }) => {
      dispatch(
        updateMessage({
          messageId,
          content: t.winwingpt.error.somethingWentWrong,
        })
      );
      dispatch(
        addMessage({
          type: "element",
          content: <RetryErrorView {...{ query, investStyle }} />,
        })
      );
    },
    [dispatch, t]
  );

  const handleReplyChart = useCallback(
    ({ chartParamsList }: { chartParamsList: ChartComponentParams[] }) => {
      if (chartParamsList.length === 0) return;
      dispatch(
        addMessage({
          type: "chart",
          content: <ReplyChartView chartParamsList={chartParamsList} />,
        })
      );
    },
    [dispatch]
  );

  const handleStreamingInterruptionError = useCallback(
    ({
      query,
      investStyle,
    }: {
      query: string;
      investStyle: Oriented | null;
    }) => {
      dispatch(
        addMessage({
          type: "element",
          content: (
            <RetryErrorView
              errorMessage={t.error.networkError}
              {...{ query, investStyle }}
            />
          ),
        })
      );
    },
    [dispatch, t]
  );

  return {
    initMessage,
    handleStreamChunk,
    handleProcessingStatus,
    handleCompleteStream,
    handleStreamError,
    handleOutputError,
    handleReplyChart,
    handleStreamingInterruptionError,
  };
};
